/**
 * Validation utilities to eliminate validation duplication across forms
 */

/**
 * Email validation
 * @param {string} email - Email to validate
 * @returns {string|null} Error message or null if valid
 */
export const validateEmail = (email) => {
  if (!email) return 'Email is required';
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address';
  }
  
  return null;
};

/**
 * Password validation
 * @param {string} password - Password to validate
 * @param {Object} options - Validation options
 * @returns {string|null} Error message or null if valid
 */
export const validatePassword = (password, options = {}) => {
  const {
    minLength = 6,
    maxLength = 128,
    requireUppercase = false,
    requireLowercase = false,
    requireNumbers = false,
    requireSpecialChars = false
  } = options;

  if (!password) return 'Password is required';
  
  if (password.length < minLength) {
    return `Password must be at least ${minLength} characters long`;
  }
  
  if (password.length > maxLength) {
    return `Password must be less than ${maxLength} characters`;
  }
  
  if (requireUppercase && !/[A-Z]/.test(password)) {
    return 'Password must contain at least one uppercase letter';
  }
  
  if (requireLowercase && !/[a-z]/.test(password)) {
    return 'Password must contain at least one lowercase letter';
  }
  
  if (requireNumbers && !/\d/.test(password)) {
    return 'Password must contain at least one number';
  }
  
  if (requireSpecialChars && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return 'Password must contain at least one special character';
  }
  
  return null;
};

/**
 * Confirm password validation
 * @param {string} confirmPassword - Confirmation password
 * @param {string} password - Original password
 * @returns {string|null} Error message or null if valid
 */
export const validateConfirmPassword = (confirmPassword, password) => {
  if (!confirmPassword) return 'Please confirm your password';
  
  if (confirmPassword !== password) {
    return 'Passwords do not match';
  }
  
  return null;
};

/**
 * Phone number validation
 * @param {string} phone - Phone number to validate
 * @returns {string|null} Error message or null if valid
 */
export const validatePhone = (phone) => {
  if (!phone) return 'Phone number is required';

  const cleaned = phone.replace(/[^\d+]/g, '');
  if (!cleaned.startsWith('+') || cleaned.length < 8) {
    return 'Enter a valid phone number with country code (e.g. +1 for US)';
  }
  return null;
};

/**
 * Required field validation
 * @param {any} value - Value to validate
 * @param {string} fieldName - Name of the field for error message
 * @returns {string|null} Error message or null if valid
 */
export const validateRequired = (value, fieldName = 'This field') => {
  if (!value || (typeof value === 'string' && value.trim() === '')) {
    return `${fieldName} is required`;
  }
  
  return null;
};

/**
 * Minimum length validation
 * @param {string} value - Value to validate
 * @param {number} minLength - Minimum length required
 * @param {string} fieldName - Name of the field for error message
 * @returns {string|null} Error message or null if valid
 */
export const validateMinLength = (value, minLength, fieldName = 'This field') => {
  if (!value) return `${fieldName} is required`;
  
  if (value.length < minLength) {
    return `${fieldName} must be at least ${minLength} characters long`;
  }
  
  return null;
};

/**
 * Maximum length validation
 * @param {string} value - Value to validate
 * @param {number} maxLength - Maximum length allowed
 * @param {string} fieldName - Name of the field for error message
 * @returns {string|null} Error message or null if valid
 */
export const validateMaxLength = (value, maxLength, fieldName = 'This field') => {
  if (!value) return null; // Let required validation handle empty values
  
  if (value.length > maxLength) {
    return `${fieldName} must be less than ${maxLength} characters`;
  }
  
  return null;
};

/**
 * URL validation
 * @param {string} url - URL to validate
 * @returns {string|null} Error message or null if valid
 */
export const validateUrl = (url) => {
  if (!url) return null; // Allow empty URLs
  
  try {
    new URL(url);
    return null;
  } catch {
    return 'Please enter a valid URL';
  }
};

/**
 * Date validation
 * @param {string|Date} date - Date to validate
 * @param {Object} options - Validation options
 * @returns {string|null} Error message or null if valid
 */
export const validateDate = (date, options = {}) => {
  const {
    required = false,
    minDate = null,
    maxDate = null,
    fieldName = 'Date'
  } = options;

  if (!date) {
    return required ? `${fieldName} is required` : null;
  }
  
  const dateObj = new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    return 'Please enter a valid date';
  }
  
  if (minDate) {
    const minDateObj = new Date(minDate);
    if (dateObj < minDateObj) {
      return `${fieldName} must be after ${minDateObj.toLocaleDateString()}`;
    }
  }
  
  if (maxDate) {
    const maxDateObj = new Date(maxDate);
    if (dateObj > maxDateObj) {
      return `${fieldName} must be before ${maxDateObj.toLocaleDateString()}`;
    }
  }
  
  return null;
};

/**
 * Number validation
 * @param {string|number} value - Value to validate
 * @param {Object} options - Validation options
 * @returns {string|null} Error message or null if valid
 */
export const validateNumber = (value, options = {}) => {
  const {
    required = false,
    min = null,
    max = null,
    integer = false,
    fieldName = 'This field'
  } = options;

  if (!value && value !== 0) {
    return required ? `${fieldName} is required` : null;
  }
  
  const num = Number(value);
  
  if (isNaN(num)) {
    return 'Please enter a valid number';
  }
  
  if (integer && !Number.isInteger(num)) {
    return 'Please enter a whole number';
  }
  
  if (min !== null && num < min) {
    return `${fieldName} must be at least ${min}`;
  }
  
  if (max !== null && num > max) {
    return `${fieldName} must be no more than ${max}`;
  }
  
  return null;
};

/**
 * User type validation
 * @param {string} userType - User type to validate
 * @returns {string|null} Error message or null if valid
 */
export const validateUserType = (userType) => {
  if (!userType) return 'User type is required';
  
  const validTypes = ['runner', 'runclub'];
  if (!validTypes.includes(userType)) {
    return 'Please select a valid user type';
  }
  
  return null;
};

/**
 * Club type validation
 * @param {string} clubType - Club type to validate
 * @returns {string|null} Error message or null if valid
 */
import { VALID_CLUB_TYPES, VALID_SKILL_LEVELS } from '../constants/clubConstants';

/**
 * Club type validation
 * @param {string} clubType - Club type to validate
 * @returns {string|null} Error message or null if valid
 */
export const validateClubType = (clubType) => {
  if (!clubType) return 'Club type is required';
  
  if (!VALID_CLUB_TYPES.includes(clubType)) {
    return 'Please select a valid club type';
  }
  
  return null;
};

/**
 * Skill level validation
 * @param {string} skillLevel - Skill level to validate
 * @returns {string|null} Error message or null if valid
 */
export const validateSkillLevel = (skillLevel) => {
  if (!skillLevel) return 'Who is this club for? is required';
  
  if (!VALID_SKILL_LEVELS.includes(skillLevel)) {
    return 'Please select who this club is for';
  }
  
  return null;
};

/**
 * Distance validation
 * @param {string|number} distance - Distance to validate
 * @returns {string|null} Error message or null if valid
 */
export const validateDistance = (distance) => {
  if (!distance) return null; // Distance is optional
  
  // Handle string distances like "5K", "10K", "Half Marathon", etc.
  if (typeof distance === 'string') {
    // Allow any non-empty string for distance
    return distance.trim().length > 0 ? null : 'Please enter a valid distance';
  }
  
  // Handle numeric distances
  const num = Number(distance);
  if (isNaN(num) || num <= 0) {
    return 'Please enter a valid distance';
  }
  
  if (num > 1000) {
    return 'Distance must be less than 1000 miles';
  }
  
  return null;
};

/**
 * Max participants validation
 * @param {string|number} maxParticipants - Max participants to validate
 * @returns {string|null} Error message or null if valid
 */
export const validateMaxParticipants = (maxParticipants) => {
  if (!maxParticipants) return 'Maximum participants is required';
  
  const num = Number(maxParticipants);
  if (isNaN(num) || !Number.isInteger(num)) {
    return 'Please enter a valid number of participants';
  }
  
  if (num < 1) {
    return 'Maximum participants must be at least 1';
  }
  
  if (num > 1000) {
    return 'Maximum participants must be less than 1000';
  }
  
  return null;
};

/**
 * Create validation rules object for useForm hook
 * @param {Object} rules - Validation rules configuration
 * @returns {Object} Validation rules object
 */
export const createValidationRules = (rules) => {
  const validationRules = {};
  
  Object.keys(rules).forEach(field => {
    const fieldRules = rules[field];
    validationRules[field] = (value) => {
      for (const rule of fieldRules) {
        const error = rule(value);
        if (error) return error;
      }
      return null;
      };
    });
  
  return validationRules;
};

/**
 * Common validation rule sets
 */
export const commonValidationRules = {
  email: [validateEmail],
  password: [validatePassword],
  confirmPassword: (value, formValues) => validateConfirmPassword(value, formValues.password),
  phone: [validatePhone],
  required: (fieldName) => (value) => validateRequired(value, fieldName),
  userType: [validateUserType],
  clubType: [validateClubType],
  skillLevel: [validateSkillLevel],
  distance: [validateDistance],
  maxParticipants: [validateMaxParticipants]
};
